import copy from 'copy-to-clipboard';
import React, {useState, useMemo} from 'react';
import {Box, Stack, styled} from '@mui/material';
import {
  Button,
  Input,
  Body,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import {generateSharedListUrl} from '@/utils/generateSharedListUrl';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import CheckboxLabel from '@/components/checkbox-label';
import {
  useAddToLists,
  useAddToListsAll,
} from '@/hooks/mutations/creators/use-add-to-lists';
import useCreateCreatorGroupLabel, {
  maxNameLength,
} from '@/hooks/mutations/use-create-creator-group-label';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useBrandCreatorGroupsQuery from '@/hooks/queries/use-brand-creator-groups-query';
import useBrandId from '@/hooks/use-brand-id';
import {ActionProps, CreatorProps} from './ActionProps';
import ContentContainer from './ContentContainer';
import SubmitButtonContainer from './SubmitButtonContainer';

const FormStack = styled(Stack)({
  gap: 8,
});

const ShareOptionsWrapper = styled(Box)({
  marginTop: 8,
});

export function useCreateAndShareList(
  creators: CreatorProps[],
  onSubmitSuccess: (result?: string) => void,
  {
    isAllFilterMatchingSelected,
    searchSessionId,
  }: {isAllFilterMatchingSelected?: boolean; searchSessionId?: string} = {}
) {
  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newListName, setNewListName] = useState('');

  // Share configuration state
  const [includePortfolio, setIncludePortfolio] = useState(true);
  const [includeNetworkAnalytics, setIncludeNetworkAnalytics] = useState(true);
  const [includePosts, setIncludePosts] = useState(true);

  // Hooks
  const brandId = useBrandId();
  const {data: agency} = useAgencyQuery({brandId});
  const {creatorGroups} = useBrandCreatorGroupsQuery({brandId});
  const creatorGroupId = creatorGroups?.[0]?.id;
  const createCreatorGroupLabel = useCreateCreatorGroupLabel(
    creatorGroupId ?? ''
  );
  const addToLists = useAddToLists();
  const addToListsAll = useAddToListsAll();

  // Validation
  const isDisabled = useMemo(() => {
    const hasNoListName =
      !newListName.trim() || newListName.length > maxNameLength;
    const hasNoShareOptions =
      !includePortfolio && !includeNetworkAnalytics && !includePosts;

    return hasNoListName || hasNoShareOptions;
  }, [newListName, includePortfolio, includeNetworkAnalytics, includePosts]);

  // Handler
  const handleSubmit = async () => {
    if (!creatorGroupId) {
      raiseFlashMessageError({
        title: translate('Something went wrong'),
        subtitle: translate('Please try again or come back later'),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // 1. Create list (always brand-level)
      const {data: newLabelData} = await createCreatorGroupLabel.mutateAsync({
        creatorGroupId,
        name: newListName,
        organizationLevel: false,
      });

      if (!newLabelData) {
        raiseFlashMessageError({
          title: translate('Failed to create list'),
          subtitle: translate('Please try again or come back later'),
        });
        return;
      }

      // 2. Add creators to list
      if (isAllFilterMatchingSelected && searchSessionId) {
        if (!agency?.id) {
          throw new Error('agency.id is missing when calling addToListsAll');
        }
        await addToListsAll.mutateAsync({
          agencyId: agency.id,
          searchSessionId,
          creatorGroupId,
          labelIds: [newLabelData.id],
        });
      } else {
        const creatorIds = creators.map((creator) => creator.id);
        const result = await addToLists.mutateAsync({
          creatorIds,
          creatorGroupId,
          labelIds: [newLabelData.id],
        });

        if (!result) {
          raiseFlashMessageError({
            title: translate('Failed to add creators to list'),
            subtitle: translate('Please try again or come back later'),
          });
          return;
        }
      }

      // 3. Generate shareable link
      const shareableUrl = generateSharedListUrl({
        refId: newLabelData.id,
        type: 'labels',
        includePosts,
        includeNetworkAnalytics,
        includePortfolio,
      });

      // 4. Copy link to clipboard and show success modal
      copy(shareableUrl);
      onSubmitSuccess(shareableUrl);
    } catch (error) {
      raiseFlashMessageError({
        title: translate('Something went wrong'),
        subtitle: translate('Please try again or come back later'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    listName: newListName,
    setListName: setNewListName,
    includePortfolio,
    setIncludePortfolio,
    includeNetworkAnalytics,
    setIncludeNetworkAnalytics,
    includePosts,
    setIncludePosts,
    isDisabled,
    isSubmitting,
    handleSubmit,
  };
}

function CreateAndShareList(props: ActionProps) {
  const {
    creators,
    onSubmitSuccess,
    isAllFilterMatchingSelected,
    searchSessionId,
  } = props;

  const {
    listName,
    setListName,
    includePortfolio,
    setIncludePortfolio,
    includeNetworkAnalytics,
    setIncludeNetworkAnalytics,
    includePosts,
    setIncludePosts,
    isDisabled,
    isSubmitting,
    handleSubmit,
  } = useCreateAndShareList(creators, onSubmitSuccess, {
    isAllFilterMatchingSelected,
    searchSessionId,
  });

  return (
    <CreateAndShareListForm
      listName={listName}
      onListNameChange={setListName}
      includePortfolio={includePortfolio}
      onIncludePortfolioChange={setIncludePortfolio}
      includeNetworkAnalytics={includeNetworkAnalytics}
      onIncludeNetworkAnalyticsChange={setIncludeNetworkAnalytics}
      includePosts={includePosts}
      onIncludePostsChange={setIncludePosts}
      isDisabled={isDisabled}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
    />
  );
}

interface CreateAndShareListFormProps {
  listName: string;
  onListNameChange: (value: string) => void;
  includePortfolio: boolean;
  onIncludePortfolioChange: (value: boolean) => void;
  includeNetworkAnalytics: boolean;
  onIncludeNetworkAnalyticsChange: (value: boolean) => void;
  includePosts: boolean;
  onIncludePostsChange: (value: boolean) => void;
  isDisabled: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
}

function CreateAndShareListForm({
  listName,
  onListNameChange,
  includePortfolio,
  onIncludePortfolioChange,
  includeNetworkAnalytics,
  onIncludeNetworkAnalyticsChange,
  includePosts,
  onIncludePostsChange,
  isDisabled,
  isSubmitting,
  onSubmit,
}: CreateAndShareListFormProps) {
  return (
    <FormStack>
      <ContentContainer>
        <Input
          autoFocus
          label={translate('List name')}
          value={listName}
          onInputChange={onListNameChange}
          clearIcon={false}
          validatorOptions={{
            onChangeText: onListNameChange,
            validators: [
              {
                validator: (val: string) => val.length <= maxNameLength,
                errorMessage: translate(
                  'Maximum length is {count} characters',
                  {
                    count: maxNameLength,
                  }
                ),
              },
            ],
          }}
        />
        <ShareOptionsSection
          includePortfolio={includePortfolio}
          onIncludePortfolioChange={onIncludePortfolioChange}
          includeNetworkAnalytics={includeNetworkAnalytics}
          onIncludeNetworkAnalyticsChange={onIncludeNetworkAnalyticsChange}
          includePosts={includePosts}
          onIncludePostsChange={onIncludePostsChange}
        />
      </ContentContainer>
      <SubmitButtonContainer>
        <Button
          appearance="neutral"
          mode="filled"
          size="large"
          disabled={isDisabled || isSubmitting}
          onClick={onSubmit}
          isLoading={isSubmitting}
          icon={
            <Icon name="Navigation-Copy" size="medium" appearance="inverse" />
          }
        >
          {translate('Create & Copy Link')}
        </Button>
      </SubmitButtonContainer>
    </FormStack>
  );
}

interface ShareOptionsSectionProps {
  includePortfolio: boolean;
  onIncludePortfolioChange: (value: boolean) => void;
  includeNetworkAnalytics: boolean;
  onIncludeNetworkAnalyticsChange: (value: boolean) => void;
  includePosts: boolean;
  onIncludePostsChange: (value: boolean) => void;
}

function ShareOptionsSection({
  includePortfolio,
  onIncludePortfolioChange,
  includeNetworkAnalytics,
  onIncludeNetworkAnalyticsChange,
  includePosts,
  onIncludePostsChange,
}: ShareOptionsSectionProps) {
  return (
    <ShareOptionsWrapper>
      <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
        {translate('Choose what to include in the shared link')}
      </Body>
      <CheckboxLabel
        checked={includePortfolio}
        onChange={() => onIncludePortfolioChange(!includePortfolio)}
        label={translate('Overview')}
      />
      <CheckboxLabel
        checked={includeNetworkAnalytics}
        onChange={() =>
          onIncludeNetworkAnalyticsChange(!includeNetworkAnalytics)
        }
        label={translate('Analytics')}
      />
      <CheckboxLabel
        checked={includePosts}
        onChange={() => onIncludePostsChange(!includePosts)}
        label={translate('Posts')}
      />
    </ShareOptionsWrapper>
  );
}

export default CreateAndShareList;
